/**
 * useDashboardKpis — Hub dashboard KPI bundle (HD-95 v3).
 *
 * v3 architecture: Hub backend reads QR's DynamoDB directly. Frontend
 * just calls `GET /v1/dashboard/kpis` on the Hub API and renders.
 *
 * No silent SSO, no cross-product tokens, no CORS gymnastics. The
 * complexity of v1/v2 paid for "loose coupling" we don't need for a
 * solo-dev single-account setup.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@d-m-organization/hub-shared-lib/auth";

// ─── Public types ───────────────────────────────────────────────────────────

export type Plan = "free" | "pro" | "business";
export type DeviceLabel = "mobile" | "desktop" | "tablet" | "other";

export interface ScansTile {
  value: number;
  delta: { percentage: number; direction: "up" | "down" | "flat" } | null;
  sparkline: number[];
}
export interface DevicesTile {
  value: number;
  label: DeviceLabel | null;
  breakdown: Array<{ label: string; count: number; percentage: number }>;
  delta: null;
}
export interface ActiveCodesTile {
  value: number;
  quota: number | null;
  label: string;
}
export interface TopCountryTile {
  code: string;
  name: string;
  flag: string;
  percentage: number;
}
export interface KpiBundle {
  window: "7d";
  generatedAt: string;
  plan: Plan;
  upgradeRequired?: true;
  tiles: {
    scans: ScansTile;
    devices: DevicesTile;
    activeCodes: ActiveCodesTile;
    topCountry: TopCountryTile | null;
  };
}

export type KpiState = "idle" | "loading" | "ready" | "error";
export interface KpiError { code: string; message: string }

export interface UseDashboardKpisResult {
  data: KpiBundle | null;
  state: KpiState;
  error: KpiError | null;
  reload: () => void;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

interface ApiOk { ok: true; data: KpiBundle }
interface ApiErr { ok: false; error: string; message?: string }
type ApiBody = ApiOk | ApiErr;

async function fetchKpis(
  apiBase: string,
  token: string,
  signal: AbortSignal,
): Promise<KpiBundle> {
  const res = await fetch(`${apiBase}/v1/dashboard/kpis`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  let body: ApiBody;
  try {
    body = (await res.json()) as ApiBody;
  } catch {
    throw makeError(`http_${res.status}`, "Unexpected response from dashboard API.");
  }
  if (!res.ok || !body.ok) {
    const err = body as ApiErr;
    throw makeError(err.error ?? `http_${res.status}`, err.message ?? "Dashboard API error.");
  }
  return (body as ApiOk).data;
}

function makeError(code: string, message: string): KpiError & Error {
  const e = new Error(message) as KpiError & Error;
  e.code = code;
  e.message = message;
  return e;
}

export function useDashboardKpis(): UseDashboardKpisResult {
  const { accessToken, isAuthReady, refreshSession } = useAuth();
  const apiBase = (import.meta as ImportMeta).env.VITE_API_BASE_URL as string | undefined;

  const [data, setData] = useState<KpiBundle | null>(null);
  const [state, setState] = useState<KpiState>("idle");
  const [error, setError] = useState<KpiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!accessToken) {
      setState("idle");
      setData(null);
      setError(null);
      return;
    }
    if (!apiBase) {
      setState("error");
      setError({ code: "config_missing", message: "API base URL not configured." });
      return;
    }

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setState("loading");
    setError(null);

    (async () => {
      try {
        const bundle = await fetchKpis(apiBase, accessToken, controller.signal);
        if (controller.signal.aborted) return;
        setData(bundle);
        setState("ready");
      } catch (err) {
        const code = (err as KpiError).code;
        // On 401, force-refresh once; effect re-runs when token updates.
        if (code === "http_401" || code === "Unauthorized") {
          try { await refreshSession(); return; } catch { /* fall through */ }
        }
        if (controller.signal.aborted) return;
        const e = err as KpiError;
        setError({ code: e.code ?? "unknown", message: e.message ?? "Failed to load KPIs." });
        setState("error");
      }
    })();

    return () => controller.abort();
  }, [apiBase, accessToken, isAuthReady, reloadKey, refreshSession]);

  return { data, state, error, reload };
}
